import { useEffect, useState } from 'react';
import { Navigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
    LayoutDashboard,
    LayoutTemplate,
    Lock,
    LucideIcon,
    MousePointerClick,
    Palette,
    PackageSearch,
    ShoppingBag,
    SlidersHorizontal,
    ToggleRight,
} from 'lucide-react';

import { cn } from '@/lib/utils';
import { Separator } from '@/components/ui/separator';
import { TopNavBar } from '../../../common/Header/TopNavBar';
import { useTabParam } from '../../../common/hooks/use-tab-param.hook';
import { useVendorPlan } from '../../../common/hooks/use-vendor-plan.hook';
import { FeatureGate } from '../../../components/FeatureGate';
import { SHELL_HEIGHT } from '../../../modules/vendor/assistant/askai-layout';
import { VisualizerPreview, FocusRegion } from '../../../modules/vendor/settings/components/VisualizerPreview';
import { GeneralSettings } from './general-settings.page';
import { AdvancedSettings } from './advanced-settings.page';
import { ProductDisplaySettings } from './product-display.page';
import { IntegrationPage } from './integration.page';
import { AutosaveReporterContext, useAutosaveReporterState } from '../../../common/hooks/use-autosave.hook';
import { AutosaveIndicator } from '../../../components/design-system/common/AutosaveIndicator';
import { StudioCanvasProvider } from './studio-canvas';
import { LayoutEditor } from '../../../modules/vendor/visualizer-layout/LayoutEditor';

/**
 * /visualizer/settings?tab=… — every setting that shapes the visualizer on the
 * vendor's site, in one editor: settings on the left, what they change on the
 * right, like the Shopping Assistant's settings.
 *
 * Two groups: the visualizer itself (brand, shopping, filters, the product
 * card) and how it runs on the site (features, product matching, the button).
 * Every section saves by itself a moment after a change, and the top bar says
 * so — one status for the whole editor.
 */
const LOOK_SECTIONS = ['layout', 'general', 'shopping', 'advanced', 'product-display'] as const;
const SITE_SECTIONS = ['features', 'matching', 'button'] as const;
const SECTIONS = [...LOOK_SECTIONS, ...SITE_SECTIONS] as const;
type Section = (typeof SECTIONS)[number];

const isSiteSection = (s: Section): s is (typeof SITE_SECTIONS)[number] =>
    (SITE_SECTIONS as readonly string[]).includes(s);

const SECTION_META: Record<Section, { icon: LucideIcon; railKey: string; rail: string; titleKey: string; descKey: string; desc: string; focus?: FocusRegion }> = {
    layout: {
        icon: LayoutDashboard, railKey: 'VISUALIZER_STUDIO.RAIL.LAYOUT', rail: 'Layout', titleKey: 'VISUALIZER_STUDIO.TITLE.LAYOUT',
        descKey: 'VISUALIZER_STUDIO.DESC.LAYOUT', desc: 'Where everything sits in your visualizer, from the room gallery to the room itself.',
    },
    general: {
        icon: Palette, railKey: 'VISUALIZER_STUDIO.RAIL.BRAND', rail: 'Brand', titleKey: 'VISUALIZER_STUDIO.TITLE.BRAND',
        descKey: 'VISUALIZER_STUDIO.DESC.GENERAL', desc: 'Your logo, colours, font and corners, as shoppers see them.', focus: 'header',
    },
    shopping: {
        icon: ShoppingBag, railKey: 'VISUALIZER_STUDIO.RAIL.SHOPPING', rail: 'Shopping', titleKey: 'VISUALIZER_STUDIO.TITLE.SHOPPING',
        descKey: 'VISUALIZER_STUDIO.DESC.SHOPPING', desc: 'Whether the visualizer is on, how shoppers order from it, its language and units, and your privacy policy.', focus: 'overview',
    },
    advanced: {
        icon: SlidersHorizontal, railKey: 'VISUALIZER_STUDIO.RAIL.FILTERS', rail: 'Filters', titleKey: 'SIDEBAR.CUSTOMIZATION.ADVANCED',
        descKey: 'VISUALIZER_STUDIO.DESC.ADVANCED', desc: 'The product fields shoppers can filter by.', focus: 'filters',
    },
    'product-display': {
        icon: LayoutTemplate, railKey: 'VISUALIZER_STUDIO.RAIL.CARD', rail: 'Card', titleKey: 'SIDEBAR.CUSTOMIZATION.PRODUCT_DISPLAY',
        descKey: 'VISUALIZER_STUDIO.DESC.PRODUCT_DISPLAY', desc: 'What each product card shows. Drag fields onto the card.',
    },
    features: {
        icon: ToggleRight, railKey: 'VISUALIZER_STUDIO.RAIL.FEATURES', rail: 'Features', titleKey: 'INTEGRATION.FEATURES.TITLE',
        descKey: 'VISUALIZER_STUDIO.DESC.FEATURES', desc: 'Which experiences run on your product pages.', focus: 'overview',
    },
    matching: {
        icon: PackageSearch, railKey: 'VISUALIZER_STUDIO.RAIL.MATCHING', rail: 'Matching', titleKey: 'INTEGRATION.MATCHING.TITLE',
        descKey: 'VISUALIZER_STUDIO.DESC.MATCHING', desc: 'How the visualizer finds the product on each page of your site.', focus: 'overview',
    },
    button: {
        icon: MousePointerClick, railKey: 'VISUALIZER_STUDIO.RAIL.BUTTON', rail: 'Design & placement', titleKey: 'INTEGRATION.BUTTON.TITLE',
        descKey: 'VISUALIZER_STUDIO.DESC.BUTTON', desc: 'Where it goes on your pages, and how it looks.',
    },
};

const RailButton = ({ section, active, locked, onSelect }: { section: Section; active: boolean; locked: boolean; onSelect: () => void }) => {
    const { t } = useTranslation();
    const meta = SECTION_META[section];
    const Icon = meta.icon;
    return (
        <button
            type="button"
            onClick={onSelect}
            aria-current={active ? 'page' : undefined}
            className={cn(
                'relative flex w-16 shrink-0 flex-col items-center gap-1 rounded-md px-1 py-2 text-[11px] leading-tight transition-colors',
                active
                    ? 'bg-muted text-foreground ring-1 ring-foreground/15'
                    : 'text-muted-foreground hover:bg-muted/60 hover:text-foreground',
            )}
        >
            <Icon className="size-[18px]" />
            <span className="w-full truncate text-center">{t(meta.railKey, meta.rail)}</span>
            {locked && <Lock className="absolute end-1 top-1 size-2.5" />}
        </button>
    );
};

const StudioBody = () => {
    const { t } = useTranslation();
    const { isFeatureLocked } = useVendorPlan();
    // Brand stays the landing section; Layout leads the rail.
    const [section, setSection] = useTabParam(SECTIONS, 'general');
    const [canvas, setCanvas] = useState<HTMLElement | null>(null);

    const meta = SECTION_META[section];
    const site = isSiteSection(section);
    const siteLocked = isFeatureLocked('apiIntegration');
    // Sections without a preview of their own show the visualizer itself.
    const ownsPreview = section === 'product-display' || section === 'button' || section === 'layout';

    // Each section starts at the top of the panel, not wherever the last one was scrolled.
    const [panel, setPanel] = useState<HTMLElement | null>(null);
    useEffect(() => { panel?.scrollTo({ top: 0 }); }, [section, panel]);

    return (
        <div className="flex min-h-0 flex-1 flex-col lg:flex-row">
            {/* ── Section rail ─────────────────────────────────────────── */}
            <nav
                aria-label={t('SIDEBAR.VISUALIZER.PARAMETERS', 'Customization') as string}
                className="flex shrink-0 items-center gap-1 overflow-x-auto border-b bg-background p-2 lg:w-[84px] lg:flex-col lg:items-center lg:overflow-y-auto lg:border-b-0 lg:border-e"
            >
                <span className="hidden pb-1 pt-1 text-[10px] font-medium uppercase tracking-wide text-muted-foreground lg:block">
                    {t('VISUALIZER_STUDIO.GROUP.LOOK', 'Visualizer')}
                </span>
                {LOOK_SECTIONS.map((s) => (
                    <RailButton key={s} section={s} active={s === section} locked={false} onSelect={() => setSection(s)} />
                ))}
                <Separator orientation="vertical" className="mx-1 h-8 lg:hidden" />
                <Separator className="my-2 hidden w-10 lg:block" />
                <span className="hidden pb-1 text-center text-[10px] font-medium uppercase leading-tight tracking-wide text-muted-foreground lg:block">
                    {t('VISUALIZER_STUDIO.GROUP.SITE_SHORT', 'On site')}
                </span>
                {SITE_SECTIONS.map((s) => (
                    <RailButton key={s} section={s} active={s === section} locked={siteLocked} onSelect={() => setSection(s)} />
                ))}
            </nav>

            {/* ── Settings ─────────────────────────────────────────────── */}
            <aside className={cn('@container flex min-h-0 w-full flex-col border-b bg-background lg:shrink-0 lg:border-b-0 lg:border-e', section === 'layout' ? 'lg:w-[540px]' : 'lg:w-[460px]')}>
                <div className="border-b px-5 py-4">
                    <p className="text-base font-medium text-foreground">{t(meta.titleKey)}</p>
                    <p className="mt-0.5 text-xs text-muted-foreground">{t(meta.descKey, meta.desc)}</p>
                </div>

                <div ref={setPanel} className="min-h-0 flex-1 overflow-y-auto p-5">
                    <StudioCanvasProvider value={canvas}>
                        {/* One element for both, so moving between them keeps unsaved edits. */}
                        {section === 'layout' && <LayoutEditor />}
                        {(section === 'general' || section === 'shopping') && (
                            <GeneralSettings inTab part={section === 'shopping' ? 'shopping' : 'brand'} />
                        )}
                        {section === 'advanced' && <AdvancedSettings inTab />}
                        {section === 'product-display' && <ProductDisplaySettings inTab />}
                        {site && (
                            <FeatureGate feature="apiIntegration">
                                {/* One element for all three, so the integration loads once and autosaves across them. */}
                                <IntegrationPage scope="visualizer" section={section} />
                            </FeatureGate>
                        )}
                    </StudioCanvasProvider>
                </div>

            </aside>

            {/* ── Preview canvas ───────────────────────────────────────── */}
            <div className="relative min-h-[640px] flex-1 overflow-auto bg-muted">
                <div
                    aria-hidden
                    className="pointer-events-none absolute inset-0 opacity-55"
                    style={{
                        backgroundImage: 'radial-gradient(circle, var(--muted-foreground) 1px, transparent 1px)',
                        backgroundSize: '20px 20px',
                    }}
                />

                <div className="sticky top-0 z-10 flex items-center justify-between gap-3 px-5 py-3">
                    <span className="rounded-md border bg-background px-2.5 py-1 text-xs text-muted-foreground">
                        {t('VISUALIZER_STUDIO.PREVIEW', 'Preview')}
                    </span>
                    <span className="hidden text-xs text-muted-foreground sm:block">
                        {t('VISUALIZER_STUDIO.AUTOSAVES', 'Changes save automatically')}
                    </span>
                </div>

                <div className="relative flex min-h-[calc(100%-60px)] items-center justify-center px-6 pb-10 pt-2">
                    <div className={cn('w-full', section === 'layout' ? 'max-w-[1180px]' : 'max-w-[860px]')}>
                        {/* Portal target for sections that draw their own preview */}
                        <div ref={setCanvas} className={cn(!ownsPreview && 'hidden')} />
                        {!ownsPreview && <VisualizerPreview focusRegion={meta.focus} />}
                    </div>
                </div>
            </div>
        </div>
    );
};

export const VisualizerStudioPage = () => {
    const { t } = useTranslation();
    const [params] = useSearchParams();
    const autosave = useAutosaveReporterState();

    // The spaces gallery needs the whole page, so it has its own.
    if (params.get('tab') === 'spaces') return <Navigate to="/visualizer/spaces" replace />;

    return (
        <div className="flex flex-col" style={{ height: SHELL_HEIGHT }}>
            <TopNavBar
                title={t('SIDEBAR.VISUALIZER.PARAMETERS', 'Customization')}
                breadcrumbs={[
                    { label: t('SIDEBAR.VISUALIZER.TITLE') },
                    { label: t('SIDEBAR.VISUALIZER.PARAMETERS', 'Customization') },
                ]}
                status={<AutosaveIndicator status={autosave.status} onRetry={autosave.retry} />}
            />
            <AutosaveReporterContext.Provider value={autosave.reporter}>
                <StudioBody />
            </AutosaveReporterContext.Provider>
        </div>
    );
};

/** /visualizer/integration?tab=button → the same section of the studio. */
export const VisualizerIntegrationRedirect = () => {
    const [params] = useSearchParams();
    const tab = params.get('tab');
    const section = tab && (SITE_SECTIONS as readonly string[]).includes(tab) ? tab : 'features';
    return <Navigate to={`/visualizer/settings?tab=${section}`} replace />;
};

export default VisualizerStudioPage;
